import React, { useState, useEffect, useCallback } from 'react';
import ForeignRegion, {
  type ForeignRegionResponseArgs,
  convertForeignRegionResponseToArgs
} from '@this/domain/god/foreign_region';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal/modal';
import { Fetcher } from '@this/src/util';
import { ConfirmModal } from '@this/components/shared/ui/feedbacks/modal/confirm_modal';
import { Button } from '@this/shared/ui/inputs/button';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Pagination } from '@this/shared/ui/navigations/pagination/pagination';
import ForeignRegionForm from './foreign_region_form';
import { formatDate } from './utils';

function getStyles() {
  return {
    inputAreaRight: {
      display: 'flex',
      marginBottom: '20px',
      justifyContent: 'flex-end'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      marginBottom: '20px'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      gap: '8px'
    } as React.CSSProperties
  } as const;
}

interface ForeignRegionsResponse {
  foreign_regions: ForeignRegionResponseArgs[];
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

const ForeignRegionsTab: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [foreignRegions, setForeignRegions] = useState<ForeignRegion[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newForeignRegion, setNewForeignRegion] = useState<ForeignRegion | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ForeignRegion | null>(null);
  const [editForeignRegion, setEditForeignRegion] = useState<ForeignRegion | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<ForeignRegion | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState<boolean>(false);

  const fetchForeignRegions = useCallback(async () => {
    setLoading(true);
    const params = {
      page: currentPage,
      per_page: pageSize
    };
    return Fetcher.get<ForeignRegionsResponse>('/god/foreign_regions', params).then(
      response => {
        const regions = response.foreign_regions.map(
          data => new ForeignRegion(convertForeignRegionResponseToArgs(data))
        );
        setLoading(false);
        setForeignRegions(regions);
        setTotalPages(response.total_pages || 1);
        setError(null);
      },
      () => {
        setLoading(false);
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      }
    );
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchForeignRegions();
  }, [fetchForeignRegions]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setNewForeignRegion(ForeignRegion.createEmpty());
    setAddError(null);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setNewForeignRegion(null);
    setAddError(null);
  }, []);

  const handleAddForeignRegion = useCallback(async () => {
    if (!newForeignRegion || !newForeignRegion.name) {
      setAddError('地域名は必須です');
      return;
    }
    setLoading(true);
    setAddError(null);
    try {
      await Fetcher.post('/god/foreign_regions', newForeignRegion.submitParams);
      closeAddModal();
      fetchForeignRegions();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setAddError(err.response?.data?.error?.join('\n') || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [newForeignRegion, closeAddModal, fetchForeignRegions]);

  const openEditModal = useCallback((foreignRegion: ForeignRegion) => {
    setShowEditModal(true);
    setEditTarget(foreignRegion);
    setEditForeignRegion(
      new ForeignRegion({
        id: foreignRegion.id,
        name: foreignRegion.name,
        ministryOfForeignAffairsId: foreignRegion.ministryOfForeignAffairsId,
        createdAt: foreignRegion.createdAt,
        updatedAt: foreignRegion.updatedAt
      })
    );
    setEditError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditTarget(null);
    setEditForeignRegion(null);
    setEditError(null);
  }, []);

  const handleEditForeignRegionValidate = useCallback(() => {
    if (!editTarget || !editForeignRegion) return;
    if (!editForeignRegion.name) {
      setEditError('地域名は必須です');
      return;
    }
    setShowEditConfirmModal(true);
  }, [editTarget, editForeignRegion]);

  const handleEditForeignRegionSubmit = useCallback(async () => {
    if (!editTarget || !editForeignRegion) return;
    setShowEditConfirmModal(false);
    setLoading(true);
    setEditError(null);
    try {
      await Fetcher.put(`/god/foreign_regions/${editTarget.id}`, editForeignRegion.submitParams);
      closeEditModal();
      fetchForeignRegions();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setEditError(err.response?.data?.error?.join('\n') || '更新に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [editTarget, editForeignRegion, closeEditModal, fetchForeignRegions]);

  const openDeleteModal = useCallback((foreignRegion: ForeignRegion) => {
    setShowDeleteModal(true);
    setDeleteTarget(foreignRegion);
    setDeleteError(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const handleDeleteForeignRegion = useCallback(async () => {
    if (!deleteTarget) return;
    setLoading(true);
    setDeleteError(null);
    try {
      await Fetcher.delete(`/god/foreign_regions/${deleteTarget.id}`);
      closeDeleteModal();
      fetchForeignRegions();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string[] } } };
      setDeleteError(err.response?.data?.error?.join('\n') || '削除に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [deleteTarget, closeDeleteModal, fetchForeignRegions]);

  const styles = getStyles();

  return (
    <div>
      <div style={styles.inputAreaRight}>
        <Button size="small" onClick={openAddModal}>
          新規追加
        </Button>
      </div>
      <div>
        {error && <div style={{ ...styles.error, whiteSpace: 'pre-wrap' }}>{error}</div>}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <Loading />
          </div>
        ) : (
          <>
            <TableContainer height="60vh">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="80px">ID</TableCell>
                    <TableCell width="200px">地域名</TableCell>
                    <TableCell width="150px">外務省ID</TableCell>
                    <TableCell width="180px">作成日時</TableCell>
                    <TableCell width="180px">更新日時</TableCell>
                    <TableCell width="150px">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {foreignRegions.length === 0 ? (
                    <TableRow>
                      <TableCell style={{ textAlign: 'center' }} colSpan={6}>
                        データがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    foreignRegions.map(region => (
                      <TableRow key={region.id}>
                        <TableCell>{region.id}</TableCell>
                        <TableCell>{region.name}</TableCell>
                        <TableCell>{region.ministryOfForeignAffairsId || '-'}</TableCell>
                        <TableCell>{formatDate(region.createdAt)}</TableCell>
                        <TableCell>{formatDate(region.updatedAt)}</TableCell>
                        <TableCell>
                          <div style={styles.actionButtonRow}>
                            <Button size="small" onClick={() => openEditModal(region)}>
                              編集
                            </Button>
                            <Button size="small" color="danger" onClick={() => openDeleteModal(region)}>
                              削除
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
            </div>
          </>
        )}
      </div>
      {newForeignRegion && (
        <Modal open={showAddModal} onClose={() => closeAddModal()}>
          <ModalHeader>海外地域 新規追加</ModalHeader>
          <ModalBody>
            <ForeignRegionForm
              foreignRegion={newForeignRegion}
              onSubmit={handleAddForeignRegion}
              onCancel={closeAddModal}
              error={addError}
              submitLabel="登録"
              cancelLabel="キャンセル"
            />
          </ModalBody>
        </Modal>
      )}
      {editForeignRegion && (
        <Modal open={showEditModal} onClose={() => closeEditModal()}>
          <ModalHeader>海外地域 編集</ModalHeader>
          <ModalBody>
            <ForeignRegionForm
              foreignRegion={editForeignRegion}
              onSubmit={handleEditForeignRegionValidate}
              onCancel={closeEditModal}
              error={editError}
              submitLabel="更新"
              cancelLabel="キャンセル"
            />
          </ModalBody>
        </Modal>
      )}
      <ConfirmModal
        open={showEditConfirmModal}
        onCancel={() => setShowEditConfirmModal(false)}
        onSubmit={handleEditForeignRegionSubmit}
        description={['顧客の設定画面の表示も変更されます。', 'それでも変更しますか？']}
        submitButtonLabel="変更する"
      />
      <ConfirmModal
        open={showDeleteModal}
        onCancel={closeDeleteModal}
        onSubmit={handleDeleteForeignRegion}
        description={
          deleteTarget
            ? deleteError
              ? [deleteError]
              : ['本当に削除しますか？', `地域名: ${deleteTarget.name}`]
            : ''
        }
        color="danger"
        submitButtonLabel="削除"
      />
    </div>
  );
};

export default ForeignRegionsTab;
